import React from "react";
import { COMPANY, VALUES, STATS } from "@/mock";
import FinalCTA from "@/components/sections/FinalCTA";

const AboutPage = () => {
  return (
    <div data-testid="about-page" className="bg-brand-bg">
      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 border-b border-brand-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-9">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-cta mb-6 flex items-center gap-3">
                <span className="block w-10 h-px bg-brand-cta" />
                About JMJC
              </p>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-brand-ink leading-[0.95]">
                A small crew. <br />
                <span className="italic font-light">Big standards.</span>
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1635221798248-8a3452ad07cd?q=85&w=1200&auto=format&fit=crop"
                  alt="Craftsman at work"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-cta mb-4">
                Our Story
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-brand-ink mb-8 leading-[1]">
                Founded in {COMPANY.founded}, built on decades of craft.
              </h2>

              <div className="space-y-6 text-base lg:text-lg text-brand-inkSoft leading-relaxed">
                <p>
                  JMJC Contracting was founded the way most good shops are
                  &mdash; one tile job at a time. We aren't trying to be the
                  biggest. We're trying to be the kind of contractor we wished
                  existed when we were the customer: one that shows up,
                  communicates clearly, and leaves homes better than they
                  found them.
                </p>
                <p>
                  Under one roof we bring together decades of hands-on
                  experience across tile, flooring, drywall, kitchens and
                  baths. We don't run six crews. We don't hand off your job to
                  whoever has Tuesday open. The same people who quote your
                  project are the ones doing the work.
                </p>
                <p>
                  We're a young company with old-school standards &mdash; and
                  most new clients come from someone pointing at a wall and
                  saying, "These are the guys."
                </p>
              </div>

              <div className="mt-10 pt-10 border-t border-brand-border">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-brand-ink flex items-center justify-center">
                    <span className="font-heading text-brand-bg font-black text-2xl leading-none">
                      J
                    </span>
                  </div>
                  <div>
                    <p className="font-bold text-brand-ink">
                      Jorge Martinez &middot; Founder
                    </p>
                    <p className="text-sm text-brand-inkSoft">
                      Master Tile Setter · 22+ years
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 lg:py-32 bg-brand-ink text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 mb-16 items-end">
            <div className="lg:col-span-7">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-cta mb-4 flex items-center gap-3">
                <span className="block w-10 h-px bg-brand-cta" />
                What we believe
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                Three rules. <br />
                <span className="italic font-light">No exceptions.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {VALUES.map((v, i) => (
              <div
                key={v.title}
                data-testid={`value-${i}`}
                className="bg-brand-ink p-10 lg:p-12 flex flex-col gap-5 min-h-[280px]"
              >
                <span className="font-heading text-5xl font-black text-brand-cta tracking-tighter">
                  0{i + 1}
                </span>
                <h3 className="font-heading text-2xl font-bold tracking-tight">
                  {v.title}
                </h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 lg:py-32 bg-brand-bg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-brand-border border border-brand-border">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                data-testid={`about-stat-${i}`}
                className="bg-brand-bg p-10 lg:p-12 flex flex-col gap-3"
              >
                <div className="font-heading text-5xl lg:text-6xl font-black tracking-tighter text-brand-cta">
                  {s.value}
                </div>
                <div className="text-xs font-medium uppercase tracking-[0.2em] text-brand-inkSoft">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
};

export default AboutPage;
